import React, { useEffect, useState } from 'react';

import { Skeleton } from '@mui/material';

import AdditionModal from '../../../../../components/AdditionModal';
import Button from '../../../../../components/Button';
import ContentCard from '../../../../../components/ContentCard';
import PermissionsList from '../../../../../components/PermissionsList';
import { useGetPermissionsQuery } from '../../../../../services/permissions';
import { Permission } from '../../../../../services/permissions/types';
import {
  useDeleteStreamPermissionRemoveMutation,
  useUpdateStreamPermissionAddMutation,
} from '../../../../../services/streams';
import { Stream } from '../../../../../services/streams/types';
import useError from '../../../../../utils/react/hooks/useError';
import useSuccess from '../../../../../utils/react/hooks/useSuccess';

interface PermissionsCardProps {
  stream?: Stream,
}

const PermissionsCard = ({ stream }: PermissionsCardProps) => {
  const [detailedStreamPermissions, setDetailedStreamPermissions] = useState<Permission[]>([]);
  const [targetPermissionId, setTargetPermissionId] = useState<string | undefined>(undefined);
  const [addPermissionModalOpen, setAddPermissionModalOpen] = useState(false);
  const [addPermissionModalSearchStr, setAddPermissionModalSearchStr] = useState('');

  const [addPermissionToStream, {
    isSuccess: isAddPermissionToStreamSuccess,
    isLoading: isAddPermissionToStreamLoading,
    isError: isAddPermissionToStreamError,
    error: addPermissionToStreamError,
    reset: resetAddPermissionToStream,
  }] = useUpdateStreamPermissionAddMutation();
  const [removePermissionFromStream, {
    isSuccess: isRemovePermissionFromStreamSuccess,
    isLoading: isRemovePermissionFromStreamLoading,
    isError: isRemovePermissionFromStreamError,
    error: removePermissionFromStreamError,
  }] = useDeleteStreamPermissionRemoveMutation();
  const { data: permissionsList } = useGetPermissionsQuery({});

  useEffect(() => {
    if (stream?.basePermissions && permissionsList) {
      const detailedPermissions = stream.basePermissions.map((p) =>
        permissionsList.data.find((x) => x.value === p),
      ).filter((x) => x !== undefined) as Permission[];

      setDetailedStreamPermissions(detailedPermissions);
    }
  }, [stream, permissionsList]);

  useSuccess(isAddPermissionToStreamSuccess, 'Permission added');
  useSuccess(isRemovePermissionFromStreamSuccess, 'Permission removed');
  useError(isAddPermissionToStreamError, addPermissionToStreamError);
  useError(isRemovePermissionFromStreamError, removePermissionFromStreamError);

  if (!stream || !permissionsList) {
    return (
      <Skeleton variant="rectangular" height={128} />
    );
  }

  return (
    <ContentCard variant="outlined" title="Permissions">
      <PermissionsList
        data={detailedStreamPermissions}
        columns={[{
          field: 'label',
          headerName: 'Label',
          width: 400,
        },
        {
          field: 'control',
          headerName: 'Control',
          width: 100,
          renderCell: ({ row }: any) => (
            <Button
              size="small"
              color="error"
              variant="text"
              allCaps
              disabled={isRemovePermissionFromStreamLoading}
              loading={isRemovePermissionFromStreamLoading
                              && targetPermissionId === row.id}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                setTargetPermissionId(row.id);
                removePermissionFromStream({
                  course: stream.course,
                  streamId: stream.streamId,
                  permissionId: row.id,
                });
              }}
            >
              Unshare
            </Button>
          ),
        }]}
        addButtonProps={{
          disabled: isRemovePermissionFromStreamLoading,
        }}
        onAddClick={() => setAddPermissionModalOpen(true)}
        pageSize={20}
        getRowHref={(row: Permission) => `/admin/permission/${row.id}`}
      />
      <AdditionModal
        title="Add Permission"
        searchPlaceholder="Permission"
        open={addPermissionModalOpen}
        handleClose={() => {
          setAddPermissionModalOpen(false);
        }}
        onAdd={(x: Permission) => addPermissionToStream({
          course: stream.course,
          streamId: stream.streamId,
          permissionId: x.value,
        })}
        isLoading={isAddPermissionToStreamLoading}
        isSuccess={isAddPermissionToStreamSuccess}
        isError={isAddPermissionToStreamError}
        error={(addPermissionToStreamError as any)?.data?.message}
        closeNotifs={resetAddPermissionToStream}
        searchFieldProps={{
          options: (permissionsList?.data || []).filter((p) => {
            const isExistingPermission = stream.basePermissions.some(
              (x) => x === p.value,
            );
            return !isExistingPermission;
          }).filter(
            (p) => p.label.toLowerCase().includes(addPermissionModalSearchStr.toLowerCase()),
          ),
          getOptionLabel: (option: Permission) => option.label,
          getOptionKey: (option: Permission) => option.value,
          onSearch: (s) => setAddPermissionModalSearchStr(s),
        }}
      />
    </ContentCard>
  );
};

export default PermissionsCard;
